'use client'

import type { ILoggedInUser } from 'types'
import Avatar from 'ui/src/components/avatar'
import ChannelAccessList from './channel-access-list'
import ChevronIcon from './chevron-icon'

const itemClassName = 'block truncate px-4 py-1.5 text-sm text-white/75 hover:text-white'
const dividerClassName = 'my-1.5 border-t border-giphyLightCharcoal/30'
const triggerClassName =
    'flex items-center gap-2 rounded-sm bg-giphyDarkCharcoal py-1 pr-3 pl-1 text-white no-underline'
const usernameClassName = 'max-w-[110px] truncate text-sm font-bold'
const panelClassName =
    'absolute top-full right-0 z-20 hidden w-[200px] rounded-sm bg-giphyDarkestGrey py-2 shadow-lg group-hover:block'

/**
 * Legacy: `channelUrl = (user as unknown as IProfileUser).profile_url ||
 * \`/${user.username}\`` (navbar-dropdown/index.tsx's `LoggedInMenu`). Used
 * for both the Collections item and this port's own trigger link (see
 * `LoggedInTrigger`'s own comment for why the trigger target was
 * unified with it).
 */
const channelUrlFor = (user: ILoggedInUser) => user.profile_url || `/${user.username}`

const LoggedInTrigger = ({ user }: { user: ILoggedInUser }) => (
    // Legacy's trigger navigates to a DIFFERENT target on click
    // (`/channel/${username}`, hardcoded) than the Collections item inside
    // its own dropdown (`profile_url || /${username}`, no `/channel`
    // prefix) — a real inconsistency in the source, not a deliberate
    // design decision worth porting. This trigger links to the same
    // `channelUrlFor` the Collections item below uses, so the two always
    // agree.
    <a href={channelUrlFor(user)} className={triggerClassName}>
        <Avatar user={user} width={32} height={32} className="shrink-0 rounded-sm" />
        <span className={usernameClassName}>{user.display_name || user.username || 'GIPHY User'}</span>
        <ChevronIcon className="shrink-0 text-white transition-transform duration-150 group-hover:rotate-180" />
    </a>
)

// Legacy's logged-out trigger fetches a random real GIF as a decorative
// avatar (`GifAvatar`, navbar-dropdown/index.tsx) via a live API call. This
// port uses the same `Avatar` component with no user data instead, which
// already falls back to a static default-avatar GIF (packages/ui's own
// `getResizedUrl`/`default${n}.gif` logic) — same visual family, no extra
// network dependency for a menu trigger.
const LoggedOutTrigger = () => (
    <div className={triggerClassName}>
        <Avatar user={{}} width={32} height={32} className="shrink-0 rounded-sm" />
        <span className={usernameClassName}>GIPHY User</span>
        <ChevronIcon className="shrink-0 text-white transition-transform duration-150 group-hover:rotate-180" />
    </div>
)

/**
 * Legacy's `LoggedInMenu` (navbar-dropdown/index.tsx) returns null entirely
 * when `user.permissions` is falsy — reproduced here even though
 * `ILoggedInUser.permissions` is typed as required, for parity with a real
 * malformed/partial payload.
 *
 * Item order and gating, verified against `LoggedInMenu` directly (all
 * match the brief with no divergence found):
 *  1. Dashboard — `permissions.dashboard_analytics`
 *  2. Collections — `channelUrlFor(user)`
 *  3. Favorites — unconditional
 *  4. Channels — `is_staff`
 *  5. Super Cut — `permissions.access_dvr || is_staff`
 *  6. Channel-access account list — `!!channel_access`
 *  7. divider
 *  8. Settings
 *  9. Log Out
 *
 * Every href is a plain `<a>`, not `next/link` — several of these
 * (`/dashboard`, `/edit/channels`, `/supercut`, `/logout`) have no Next
 * route at all yet and are Django-only, matching the brief's "no Next route
 * ... same for the other destinations."
 */
const LoggedInItems = ({ user }: { user: ILoggedInUser }) => {
    if (!user.permissions) {
        return null
    }

    const { is_staff, permissions, channel_access } = user
    const { dashboard_analytics, access_dvr } = permissions

    return (
        <ul>
            {dashboard_analytics && (
                <li>
                    <a href="/dashboard" className={itemClassName}>
                        Dashboard
                    </a>
                </li>
            )}
            <li>
                <a href={channelUrlFor(user)} className={itemClassName}>
                    Collections
                </a>
            </li>
            <li>
                <a href="/favorites" className={itemClassName}>
                    Favorites
                </a>
            </li>
            {is_staff && (
                <li>
                    <a href="/edit/channels" className={itemClassName}>
                        Channels
                    </a>
                </li>
            )}
            {(access_dvr || is_staff) && (
                <li>
                    <a href="/supercut" className={itemClassName}>
                        Super Cut
                    </a>
                </li>
            )}
            {!!channel_access && <ChannelAccessList user={user} channelAccess={channel_access} />}
            <li className={dividerClassName} role="separator" />
            <li>
                <a href="/settings" className={itemClassName}>
                    Settings
                </a>
            </li>
            <li>
                <a href="/logout" className={itemClassName}>
                    Log Out
                </a>
            </li>
        </ul>
    )
}

/**
 * Legacy's `LoggedOutMenu` (navbar-dropdown/index.tsx) — three items, no
 * gating. Not reproduced: legacy's further collapse to a bare "Log in"
 * link when there are zero anonymous local favorites (`justLogin`,
 * `Login`'s own render) — that state lives in a client-only localStorage
 * hook (`context/favorites-manager.tsx`'s `local-favorites` key) with no
 * server-side signal, and the brief's own description of the logged-out
 * variant names only these three items, not that further collapse.
 */
const LoggedOutItems = () => (
    <ul>
        <li>
            <a href="/login" className={itemClassName}>
                Login
            </a>
        </li>
        <li>
            <a href="/join" className={itemClassName}>
                Join
            </a>
        </li>
        <li>
            <a href="/favorites" className={itemClassName}>
                Favorites
            </a>
        </li>
    </ul>
)

type Props = { user?: ILoggedInUser }

/**
 * The user-menu portion of legacy's minimal `/create` header
 * (navbar-dropdown/index.tsx's `Login` component) — GIPHY logo is out of
 * scope for this task. Mounted once per create step via
 * `gifmaker/layout.tsx`, outside `CreationShell`'s measured `mx-auto
 * w-[1040px]` container so it cannot shift anything
 * `upload-measurements.spec.ts`/`video-trim-measurements.spec.ts` measure
 * (both compare element position relative to that container's own box, not
 * the viewport — see their own header comments).
 *
 * Hover-driven dropdown (`group`/`group-hover:block`), matching both
 * legacy's own `:hover` CSS trigger (style.tsx's `Container:hover
 * ${MenuContainer}`) and this app's existing pop-up-menu.tsx idiom — not
 * `components/nav/desktop/*`, which is caret-position-computed off the
 * full nav row and out of bounds per the brief.
 */
const UserMenu = ({ user }: Props) => (
    <header className="flex w-full justify-end px-6 py-3">
        <div className="group relative">
            {user ? <LoggedInTrigger user={user} /> : <LoggedOutTrigger />}
            <div className={panelClassName}>{user ? <LoggedInItems user={user} /> : <LoggedOutItems />}</div>
        </div>
    </header>
)

export default UserMenu
